import { useEffect, useState } from "react";
import { X, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TipJar({ open, onClose }) {
  const { user } = useAuth();
  const [tips, setTips] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const r = await api.get("/payments/catalog"); setTips(r.data.tip_packages || []); } catch { /* ignore */ }
    })();
  }, [open]);

  const tip = async (lookup_key) => {
    if (!user) { toast.error("Please sign in"); return; }
    setBusy(lookup_key);
    try {
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" data-testid="tip-jar">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md jx-glass rounded-t-3xl p-5 pb-8 jx-fade-up">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-300 flex items-center justify-center">
              <Heart size={18} fill="currentColor" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg">Keep Jixxeo free</p>
              <p className="text-[11px] text-slate-400 font-mono-data uppercase tracking-widest">A tip from a human · goes a long way</p>
            </div>
          </div>
          <button data-testid="tip-close" onClick={onClose} className="text-slate-400 p-1"><X size={20} /></button>
        </div>

        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
          Jixxeo Scan is free and ad-free. If it saved you a search, a trip to the garden store or a repair bill, drop us a tip. Every supporter earns +50 coins and a lifetime Supporter badge.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {tips.map((t) => (
            <button
              key={t.id}
              data-testid={`tip-${t.id}`}
              onClick={() => tip(t.lookup_key)}
              disabled={busy === t.lookup_key}
              className="jx-glass rounded-2xl p-3 active:scale-95 disabled:opacity-60 border border-white/10 hover:border-rose-400/50 text-left transition-colors"
            >
              <div className="text-3xl">{t.emoji}</div>
              <p className="mt-1 font-heading font-bold">${t.price.toFixed(0)}</p>
              <p className="text-[11px] text-slate-400">{t.label}</p>
              {busy === t.lookup_key && <Loader2 className="mt-1 animate-spin text-slate-400" size={14} />}
            </button>
          ))}
        </div>

        {user?.supporter && (
          <div className="mt-4 rounded-xl bg-rose-500/10 border border-rose-400/30 p-2 text-center">
            <p className="text-xs text-rose-200"><Heart size={11} className="inline" fill="currentColor" /> You're already a Supporter — thank you.</p>
          </div>
        )}

        <p className="mt-4 text-center text-[10px] text-slate-500 font-mono-data uppercase tracking-widest">
          Secure payments by Stripe · No subscription
        </p>
      </div>
    </div>
  );
}
